/*
 * SpendCharts — "Spending insights" for the dashboard.
 *
 * Three single-series horizontal bar charts computed from this month's items:
 * spend by category, top expenses, and spend by shop/place. Pure inline markup
 * (no chart lib): thin rounded bars, one hue per chart, value labels, sorted
 * high→low, hover shows the exact figure. Renders nothing until there's spend.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { fmtR, unitRate, spentOf } from '../money.js';

function Bars({ rows, hue }) {
  if (!rows.length) return <p className="text-[11px] text-slate-600">Nothing yet.</p>;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="grid gap-2">
      {rows.map((r) => (
        <div key={r.label} className="grid gap-1 min-w-0" title={`${r.label}: ${fmtR(r.value)}${r.visits ? ` · ${r.visits} visits` : ''}`}>
          <div className="flex items-baseline justify-between gap-2 text-[11px] min-w-0">
            {/* min-w-0 + truncate: the name (with its "· N visits" suffix) clips with an
                ellipsis instead of running under the right-aligned, shrink-0 amount. */}
            <span className="truncate text-slate-300 min-w-0 flex-1">{r.label}{r.visits ? <span className="text-slate-500"> · {r.visits} visit{r.visits === 1 ? '' : 's'}</span> : null}</span>
            <span className="text-slate-400 shrink-0 tabular-nums">{fmtR(r.value)}</span>
          </div>
          <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
            <div className="h-full rounded-full"
                 style={{ width: `${Math.max(3, (r.value / max) * 100)}%`, background: hue }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SpendCharts({ items = [], visits = [] }) {
  const spent = items.filter((i) => i.kind === 'expense' && i.source !== 'transfer'
                                    && spentOf(i) > 0);
  if (!spent.length) return null;

  const group = (keyFn) => {
    const m = {};
    for (const e of spent) { const k = keyFn(e); if (!k) continue; m[k] = (m[k] || 0) + spentOf(e); }
    return Object.entries(m).map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value).slice(0, 8);
  };
  const byCat = group((e) => e.category_name || 'Uncategorised');
  // Visit counts per place (Pass 7) — attached to the shop rows so a habit shows
  // both what it cost and how often you went.
  const visitBy = {};
  (visits || []).forEach((v) => { if (v.place_name) visitBy[v.place_name] = (visitBy[v.place_name] || 0) + 1; });
  const byShop = group((e) => e.place_name || null).map((r) => ({ ...r, visits: visitBy[r.label] || 0 }));
  const byExpense = spent.map((e) => ({ label: e.title, value: spentOf(e) }))
    .sort((a, b) => b.value - a.value).slice(0, 8);
  const total = spent.reduce((a, e) => a + spentOf(e), 0);

  return (
    <div className="card !p-5 mt-4" data-guide="spend-insights">
      <div className="flex items-baseline justify-between">
        <h2 className="font-bold">Spending insights</h2>
        <span className="text-xs text-slate-500">{fmtR(total)} spent this month</span>
      </div>
      {/* auto-fit + minmax(200,1fr): bounded, self-collapsing tracks (3-up on desktop,
          stacking under ~380px) so a long name can no longer spill into the next column. */}
      <div className="grid gap-6 mt-4" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))' }}>
        <div className="min-w-0">
          <h3 className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">By category</h3>
          <Bars rows={byCat} hue="#3db8f5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Top expenses</h3>
          <Bars rows={byExpense} hue="#8B5CF6" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">By shop / place</h3>
          {byShop.length
            ? <Bars rows={byShop} hue="#34d399" />
            : <p className="text-[11px] text-slate-600">Link expenses to shops on the Map to see this.</p>}
        </div>
      </div>
    </div>
  );
}
